import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import StripeConnectCard from "@/components/affiliate/StripeConnectCard";

export default function PublisherPayouts() {
  const [overview, setOverview] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ amount: 0, method: "bank_transfer" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [o, p] = await Promise.all([api.get("/publisher/overview"), api.get("/publisher/payouts")]);
      setOverview(o.data);
      setPayouts(p.data);
    } catch (e) {
      toast.error("Failed to load payouts");
    }
  };
  useEffect(() => { refresh(); }, []);

  const requestPayout = async () => {
    setBusy(true);
    try {
      await api.post("/publisher/payouts/request", payoutForm);
      toast.success("Payout requested · we'll process within 2 business days");
      setPayoutOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout request failed");
    } finally {
      setBusy(false);
    }
  };

  if (!overview) return <div className="min-h-[40vh] grid place-items-center text-sm text-slate-500"><Loader2 className="size-4 animate-spin" /></div>;
  const k = overview.kpis;

  return (
    <div className="space-y-6" data-testid="publisher-payouts-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Payouts</h1>
        <p className="text-[13px] text-slate-500 mt-1">Withdraw your earnings via Stripe Connect or bank transfer.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Wallet className="size-4 text-[#0055FF]" /> Balance</h2>
            <Button size="sm" onClick={() => setPayoutOpen(true)} disabled={k.available_balance_gbp < 1} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="pub-request-payout">Request payout</Button>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <Row k="Gross earnings" v={`£${k.earnings_total_gbp.toLocaleString()}`} />
            <Row k="Paid out" v={`£${k.paid_out_gbp.toLocaleString()}`} />
            <div className="border-t border-slate-200 pt-2 flex items-center justify-between font-medium">
              <span className="text-slate-700">Available now</span>
              <span className="text-slate-900 tabular-nums">£{k.available_balance_gbp.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <StripeConnectCard kind="publisher" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">History</h2>
        {payouts.length === 0 ? (
          <p className="text-[12.5px] text-slate-400 italic">No payouts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pub-payouts-table">
              <thead className="text-[11px] uppercase tracking-widest text-slate-500"><tr className="text-left">
                <th className="py-2">Date</th><th>Method</th><th className="text-right">Amount</th><th>Status</th>
              </tr></thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="py-2">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                    <td>{p.method?.replace(/_/g, " ")}</td>
                    <td className="text-right font-mono">£{Number(p.amount_gbp).toLocaleString()}</td>
                    <td><span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${p.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request payout</DialogTitle>
            <DialogDescription>Available: £{k.available_balance_gbp.toLocaleString()} · paid within 2 business days.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Amount (£)</label>
              <Input type="number" min={1} max={k.available_balance_gbp} value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: parseFloat(e.target.value || "0") })} data-testid="payout-amount" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Method</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setPayoutForm({ ...payoutForm, method: "bank_transfer" })} className={`rounded-xl border px-3 py-2 text-[13px] text-left transition-all ${payoutForm.method === "bank_transfer" ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200"}`}>Bank transfer</button>
                <button type="button" onClick={() => setPayoutForm({ ...payoutForm, method: "stripe_connect" })} className={`rounded-xl border px-3 py-2 text-[13px] text-left transition-all ${payoutForm.method === "stripe_connect" ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200"}`}>Stripe Connect</button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPayoutOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={requestPayout} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="payout-submit">{busy ? "Submitting…" : <>Request <ArrowRight className="size-4" /></>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 text-[12.5px]">{k}</span>
      <span className="text-slate-900 text-[13px] font-medium tabular-nums">{v}</span>
    </div>
  );
}
